import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slice/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/report';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Energy report trend chart
 * This component displays the energy consumption trend over time.
 */
function EnergyReportTrendChart() {
  const { t } = useTranslation('reports');
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const reportsData = useAppSelector((state) => state.reportData);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#00A79D',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Returns the tooltip title.
           * @returns The tooltip title string.
           */
          title: () => `${t('powerConsumed')} (kWh)`,
          /**
           * Formats the tooltip label using locale string.
           * @param context - The tooltip context object.
           * @returns The formatted label string.
           */
          label: (context: any) => `${context.raw.toLocaleString()} kWh`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param range - The time range for the report ('last24hours', 'last7days', 'lastMonth', 'last12months').
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'powerConsumed',
      });

      let chartData: any[] = [];

      if (response.status === 200) {
        if (range === 'last12months') {
          chartData = fillMissingMonthsGeneric(response.data);
        } else if (range === 'lastMonth') {
          chartData = fillMissingLastNDaysGeneric(response.data, 30);
        } else if (range === 'last7days') {
          chartData = fillMissingLastNDaysGeneric(response.data, 7);
        } else if (range === 'last24hours') {
          chartData = fillMissingLast24HrsGeneric(response.data);
        }
        chartData = chartData.map((item) => ({
          ...item,
          totalPowerConsumed: item.totalPowerConsumed / 1000,
        }));
      }

      setBarGraphData(chartData);
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };
  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  const labels = barGraphData.map((d) => d.month || d.label);
  const values = barGraphData.map((d) => d.totalPowerConsumed || 0);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('powerConsumed') ? 'active' : ''}`}
              onClick={() => handleClick('powerConsumed')}>
              {reportsData.activePinName.includes('powerConsumed') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('energyUsageTrend')}
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((item) => t(item.label))}
            onToggle={(index) => {
              const selected = chartTimePeriod[index];
              if (selected) fetchPowerConsumedReport(selected.value);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>

        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: t('powerConsumed'),
                    data: values,
                    borderColor: '#00A79D',
                    backgroundColor: '#00A79D',
                    fill: false,
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBackgroundColor: '#00A79D',
                    pointBorderColor: '#00A79D',
                  },
                ],
              }}
              options={chartOptions}
            />
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default EnergyReportTrendChart;
